import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Capacitor } from '@capacitor/core';
import { logError } from '@/lib/log';
import { createSubscription } from '@shared/services/stripeService';

/**
 * Shared Stripe Elements checkout step: enter payment details → create the
 * subscription → confirm payment. Used by both the legacy `DataSyncPaywall`
 * ($4–$12 pick-your-price, no trial) and the paid-launch `SpendingToolsPaywall`
 * web path (card T2 — fixed $15/mo · $100/yr + `trial_period_days`), so the two
 * paywalls share one Stripe integration instead of maintaining two.
 */

// Initialize Stripe outside any component to avoid re-creation.
export const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null;

// Stripe Elements appearance themed to match brand.
export const stripeAppearance = {
  theme: 'flat' as const,
  variables: {
    colorPrimary: '#1D3A29',       // growth
    colorBackground: '#FFF8F0',    // sand
    colorText: '#1D3A29',          // growth
    colorDanger: '#961B1B',        // wildfire
    fontFamily: 'Poppins, system-ui, sans-serif',
    borderRadius: '12px',
    spacingUnit: '4px',
  },
};

export interface StripeCheckoutStepProps {
  /** Stripe Price ID to subscribe to. */
  priceId: string;
  /** Dollar amount — only used to size the Stripe `Elements` payment-element; the actual charge is driven server-side by `priceId`. */
  amount: number;
  /** Optional promotion code (legacy coupon input). */
  promotionCode?: string;
  /** Ask the server to apply the paid-launch trial (card T2 / PAID_LAUNCH_PLAN.md §4b). The server may still decline (e.g. `freeTrialUsed`) — read `trialApplied` in `onSuccess`, not this flag. */
  requestTrial?: boolean;
  /** Button label while idle, e.g. "Start free trial" or "Join". */
  ctaLabel: string;
  /** Called once payment is confirmed. `trialApplied` reflects the server's actual decision (undefined when the caller never requested a trial). */
  onSuccess: (info: { trialApplied?: boolean }) => void;
  onBack: () => void;
}

/** Wraps the inner form in an `<Elements>` provider sized to `amount`. Renders nothing if Stripe isn't configured. */
export const StripeCheckoutStep: React.FC<StripeCheckoutStepProps> = ({ amount, ...formProps }) => {
  if (!stripePromise) return null;
  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: 'subscription',
        amount: amount * 100,
        currency: 'usd',
        appearance: stripeAppearance,
        paymentMethodConfiguration: import.meta.env.VITE_STRIPE_PAYMENT_METHOD_CONFIG,
      }}
    >
      <StripePaymentForm {...formProps} />
    </Elements>
  );
};

/**
 * Inner component that uses useStripe/useElements hooks.
 * Must be rendered inside an <Elements> provider with mode: 'subscription'.
 * Flow: validate → create subscription → confirm payment with client secret.
 */
const StripePaymentForm: React.FC<Omit<StripeCheckoutStepProps, 'amount'>> = ({
  priceId,
  promotionCode,
  requestTrial,
  ctaLabel,
  onSuccess,
  onBack,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [inlineError, setInlineError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setInlineError('');

    try {
      // 1. Validate payment details
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setInlineError(submitError.message ?? 'Please check your payment details');
        setIsProcessing(false);
        return;
      }

      // 2. Create subscription (returns client secret or auto-activates)
      const result = await createSubscription(priceId, promotionCode, requestTrial ? { requestTrial: true } : undefined);

      if (result.autoActivated) {
        onSuccess({ trialApplied: result.trialApplied });
        return;
      }

      // 3. Confirm with the returned client secret. A trialing subscription
      // has a $0 first invoice, so the server hands back a SETUP intent
      // secret (`seti_…`) for card collection instead of a payment intent —
      // that must go through confirmSetup, not confirmPayment. Elements
      // `mode: 'subscription'` (the deferred-intent mode) supports both.
      const confirmParams = {
        return_url: Capacitor.isNativePlatform() ? 'https://my.enough.app' : window.location.origin,
      };
      const clientSecret = result.clientSecret!;
      const { error } = clientSecret.startsWith('seti_')
        ? await stripe.confirmSetup({
            elements,
            clientSecret,
            confirmParams,
            redirect: 'if_required',
          })
        : await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams,
            redirect: 'if_required',
          });

      if (error) {
        setInlineError(error.message ?? 'Payment failed');
      } else {
        onSuccess({ trialApplied: result.trialApplied });
      }
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      const friendly = raw && !raw.match(/^(Request\s|req_)/i) ? raw : 'Something went wrong. Please try again.';
      setInlineError(friendly);
      logError(err, { tags: { operation: 'stripe_payment' } });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="ph-no-capture">
        <PaymentElement />
      </div>

      {inlineError && (
        <div className="p-3 bg-wildfire/10 border border-wildfire/20 rounded-xl text-sm text-wildfire">
          {inlineError}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className={`w-full py-3.5 rounded-xl font-medium transition-colors ${
          !stripe || isProcessing
            ? 'bg-mist text-growth/50 cursor-not-allowed'
            : 'bg-growth text-cloud hover:bg-growth/90'
        }`}
      >
        {isProcessing ? 'Processing...' : ctaLabel}
      </button>

      <button
        type="button"
        onClick={onBack}
        disabled={isProcessing}
        className="w-full text-sm text-growth/50 hover:text-growth transition-colors py-2"
      >
        Back to pricing
      </button>
    </form>
  );
};
